'use client';

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ConnectorType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AddConnectorWizardState } from '../addConnectorWizardState';

export const AddGcpConnectorStep = (
  props: StepProps<AddConnectorWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [acknowledged, setAcknowledged] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<AddConnectorWizardState>) =>
    setWizardState({ ...state, ...partial });

  const isMissingFields =
    !state.gcpProjectId ||
    !state.discoveryServiceAccountName ||
    !state.dataAccessServiceAccountName;

  const canCreate = !isMissingFields && acknowledged && !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      await dal.lakehouse.createConnector(state.workspaceId, {
        name: state.connectorName,
        connectorType: ConnectorType.CONNECTOR_TYPE_GCP_PROJECT_CONNECTOR,
        gcpProjectProperties: {
          gcpProjectId: state.gcpProjectId,
          discoveryServiceAccountName: state.discoveryServiceAccountName,
          dataAccessServiceAccountName: state.dataAccessServiceAccountName,
        },
      });
      void props.finish();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create connector'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create'}
      nextButtonLoadingText='Creating...'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Connector Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Connector Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the GCP project this connector connects to.
              </Typography>
            </Stack>

            <FormTextField
              title='GCP Project ID'
              size='small'
              placeholder='e.g., my-gcp-project-123'
              value={state.gcpProjectId}
              onChange={(e) => update({ gcpProjectId: e.target.value })}
            />
          </Stack>
        </Box>

        {/* Service Accounts */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Service Accounts</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the GCP service account names that Eon will use in your
                GCP project for discovery and data access.
              </Typography>
            </Stack>

            <FormTextField
              title='Discovery Service Account Name'
              size='small'
              placeholder='e.g., eon-datalake-discovery-sa'
              value={state.discoveryServiceAccountName}
              onChange={(e) =>
                update({ discoveryServiceAccountName: e.target.value })
              }
            />

            <FormTextField
              title='Data Access Service Account Name'
              size='small'
              placeholder='e.g., eon-datalake-data-access-sa'
              value={state.dataAccessServiceAccountName}
              onChange={(e) =>
                update({ dataAccessServiceAccountName: e.target.value })
              }
            />
          </Stack>
        </Box>

        {/* Deployment Confirmation */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Deployment</Typography>
              <Typography variant='body2' color='text.secondary'>
                Confirm that the required service accounts have been configured
                in your GCP project before creating the connector.
              </Typography>
            </Stack>

            {state.gcpProjectId && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  Ensure the following service accounts are configured in GCP
                  project <strong>{state.gcpProjectId}</strong> with the
                  required permissions for Eon:
                </Typography>
                <Typography
                  variant='body2'
                  component='ul'
                  sx={{ mt: '8px', pl: '16px' }}
                >
                  <li>
                    <code>{state.discoveryServiceAccountName}</code> (discovery)
                  </li>
                  <li>
                    <code>{state.dataAccessServiceAccountName}</code> (data
                    access)
                  </li>
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='body2'>
                  I have configured the service accounts in my GCP project
                </Typography>
              }
            />
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
